import { Trans, useTranslation } from 'react-i18next';
import {
  ButtonProps,
  formatNumber,
  InputProps,
  isSafariBrowser,
  useClickAway,
  useMatchMedia,
} from '@make-software/cspr-design';
import React, { useState } from 'react';
import { PaginationInfoText } from './pagination-info-text';
import styled from 'styled-components';

interface PaginationInputProps extends ButtonProps {
  currentPage: number;
  pageCount: number;
  onChange: (page) => void;
}

const PaginationInputContainer = styled.div(({ theme }) =>
  theme.withMedia({
    width: ['100%', '160px'],
  })
);

const StyledInput = styled('input')<InputProps>(({ theme }) => ({
  color: 'inherit',
  background: 'inherit',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  border: 'none',
  width: '100%',
  padding: 0,
  textAlign: 'center',
  caretColor: theme.styleguideColors.contentRed,
  '&[type=number]': {
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      margin: 0,
      '-webkit-appearance': 'none',
      'pointer-events': 'none',
    },
  },
}));

const InputInfoText = styled(PaginationInfoText)(({ theme }) => ({
  width: '100%',
  cursor: 'pointer',
  ':hover': {
    background: theme.styleguideColors.fillSecondaryRedHover,
    color: theme.styleguideColors.contentRed,
  },
}));

export const PaginationInput = ({
  currentPage,
  pageCount,
  onChange,
}: PaginationInputProps) => {
  const { t } = useTranslation();
  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);

  const [isHovered, setHover] = useState<boolean>(false);
  const [showInput, setShowInput] = useState<boolean>(false);
  const [page, setPage] = useState(undefined);

  const convertDecimalToThousand = (value) => {
    return Number(value?.replace(/[,.]/g, '')) || 0;
  };

  const resetInputValue = () => {
    setShowInput(false);
    setPage(undefined);
  };

  const { ref } = useClickAway({
    callback: () => {
      resetInputValue();
    },
  });

  const handleMouseOver = () => {
    setHover(true);
  };

  const handleMouseOut = () => {
    setHover(false);
  };

  const handleClick = () => {
    setShowInput(true);
  };

  const handleKeyDown = (ev) => {
    if (ev.key === 'Enter') {
      setShowInput(true);
    }
  };

  const handleChangePage = () => {
    const pageNumber = convertDecimalToThousand(page);

    onChange(pageNumber > pageCount ? pageCount : pageNumber);
    resetInputValue();
  };

  const handleSubmitOnSafariMobile = () => {
    if (isSafariBrowser && screenType == 'mobile') {
      handleChangePage();
    }
  };

  const handleSubmit = (e) => {
    if (e.keyCode === 13) {
      handleChangePage();
    }
  };

  return (
    <PaginationInputContainer
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleClick}
      ref={ref}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <InputInfoText>
        {showInput ? (
          <StyledInput
            inputMode={'numeric'}
            autoFocus
            value={page}
            onChange={(e) => setPage(e.target.value)}
            onKeyDown={handleSubmit}
            onBlur={handleSubmitOnSafariMobile}
          />
        ) : isHovered && !showInput ? (
          <Trans t={t}>Enter page</Trans>
        ) : (
          <Trans t={t}>
            Page {{ page: formatNumber(currentPage) }} of{' '}
            {{ totalPages: formatNumber(pageCount) }}
          </Trans>
        )}
      </InputInfoText>
    </PaginationInputContainer>
  );
};
